import { useAlertContext } from "@shared";
import { useMutation, useMutationState, useQueryClient } from "@tanstack/react-query";

import { internetRegistrationsApi } from "@/api";
import { QueryKey } from "@/query/Query";
import { useTranslation } from "@/translations";

const dismissDuplicateMutationKey = [QueryKey.InternetRegistrations, "dismiss-duplicate"];

/**
 * The decisions a reviewer can take on a pending Internetanmeldung. Only "Kein Duplikat" exists so
 * far; "Fusionieren" is the separate Kund:innen-Fusionierung story.
 */
export function useInternetRegistrationMutations() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { showSuccess } = useAlertContext();

  const refreshList = () => queryClient.invalidateQueries({ queryKey: [QueryKey.InternetRegistrations] });

  // The client list is refreshed too, but nothing on this page waits for it: the dismissed registration
  // loses its IA status, which the client search shows and filters on.
  const refreshClients = () => void queryClient.invalidateQueries({ queryKey: [QueryKey.Clients] });

  const dismissDuplicate = useMutation({
    mutationKey: dismissDuplicateMutationKey,
    mutationFn: (clientId: string) => internetRegistrationsApi.dismissInternetRegistrationDuplicate({ clientId }),
    // Awaited, so the entry's buttons stay locked until it has actually left the list. Returning the
    // refetch here rather than firing it off keeps a second click from reaching a decision already taken.
    onSuccess: async () => {
      showSuccess(t("internetRegistrations.messages.dismissed"));
      refreshClients();
      await refreshList();
    },
    // A colleague may have decided the same entry first; re-reading drops it from this list too.
    onError: async () => {
      refreshClients();
      await refreshList();
    },
  });

  // Each entry locks on its own decision. A single mutation only remembers its latest input, so
  // deciding a second entry would unlock the first one while its request is still in flight.
  const decidingClientIds = useMutationState({
    filters: { mutationKey: dismissDuplicateMutationKey, status: "pending" },
    select: (mutation) => mutation.state.variables as string,
  });

  return { dismissDuplicate, decidingClientIds };
}
